import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Target, Users, Lightbulb, Rocket, TrendingUp, Shield, Calendar, Star, 
  BarChart3, Search, Zap, MousePointer, Megaphone, Eye, Building, 
  GraduationCap, HelpCircle, RefreshCw, Smartphone, FileText, Trophy,
  ChevronRight, Circle
} from 'lucide-react';

const goals = [
  { value: 'convert-visitors', title: 'Convert Visitors', description: 'Turn more visitors into qualified leads', icon: Target },
  { value: 'lead-generation', title: 'Lead Generation', description: 'Help sales team book more demos', icon: Users },
  { value: 'simplify-product', title: 'Simplify Product', description: 'Explain complex features simply', icon: Lightbulb },
  { value: 'launch-product', title: 'Launch Product', description: 'Make a powerful market entry', icon: Rocket },
  { value: 'raise-funding', title: 'Raise Funding', description: 'Pitch to investors effectively', icon: TrendingUp },
  { value: 'build-trust', title: 'Build Trust', description: 'Show reliability and customer success', icon: Shield },
  { value: 'event-success', title: 'Event Success', description: 'Boost registration and engagement', icon: Calendar },
  { value: 'success-stories', title: 'Success Stories', description: 'Let customers convince prospects', icon: Star },
  { value: 'market-expansion', title: 'Market Expansion', description: 'Reach new verticals', icon: BarChart3 },
  { value: 'rank-google', title: 'Rank in Google', description: 'Boost search visibility with video', icon: Search },
  { value: 'speed-onboarding', title: 'Speed Onboarding', description: 'Help new users succeed faster', icon: Zap },
  { value: 'improve-ctr', title: 'Improve CTR', description: 'Boost click-through rates on ads', icon: MousePointer },
  { value: 'social-presence', title: 'Social Presence', description: 'Build engaging social media content', icon: Megaphone },
  { value: 'brand-awareness', title: 'Brand Awareness', description: 'Establish industry leadership', icon: Eye },
  { value: 'company-culture', title: 'Company Culture', description: 'Attract talent and partners', icon: Building },
  { value: 'train-users', title: 'Train Users', description: 'Onboard and upskill teams', icon: GraduationCap },
  { value: 'reduce-support', title: 'Reduce Support', description: 'Clarify common questions', icon: HelpCircle },
  { value: 'product-updates', title: 'Product Updates', description: 'Announce features effectively', icon: RefreshCw },
  { value: 'app-downloads', title: 'App Downloads', description: 'Increase mobile app installations', icon: Smartphone },
  { value: 'shareholder-report', title: 'Shareholder Report', description: 'Engage investors effectively', icon: FileText },
  { value: 'stand-out', title: 'Stand Out', description: 'Differentiate your brand', icon: Trophy }
];

const StepOne = ({ selectedGoal, onGoalSelect, onNext }) => {
  const [hoveredGoal, setHoveredGoal] = useState(null);

  const activeGoal = useMemo(() => {
    if (hoveredGoal) return goals.find(g => g.value === hoveredGoal);
    if (selectedGoal) return goals.find(g => g.value === selectedGoal);
    return goals[0];
  }, [hoveredGoal, selectedGoal]);

  const radius = 250;
  const totalGoals = goals.length;
  const angleStep = 360 / totalGoals;

  const getPosition = (index) => {
    const angle = (index * angleStep - 90) * (Math.PI / 180);
    return {
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
      angle: index * angleStep
    };
  };

  return (
    <div className="flex flex-col lg:flex-row items-center justify-center gap-12 min-h-[700px] w-full relative overflow-hidden">
      
      {/* HUD Circle (Left Side) */}
      <div className="relative w-[550px] h-[550px] shrink-0 scale-[0.6] sm:scale-[0.8] md:scale-[0.9] lg:scale-100 transition-transform duration-500">
        
        {/* Anti-clockwise Background Glow Ring */}
        <motion.div 
          animate={{ rotate: -360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute inset-[-40px] rounded-full border border-amber-500/5 opacity-30 shadow-[inset_0_0_100px_rgba(123,97,53,0.1)]"
        />

        {/* Outer Tech Rings */}
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 rounded-full border border-dashed border-amber-500/20"
        />
        <motion.div 
          animate={{ rotate: -360 }}
          transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
          className="absolute inset-[30px] rounded-full border border-amber-500/10"
        />

        {/* Center Data Core */}
        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
          <AnimatePresence mode="wait">
            <motion.div 
              key={activeGoal.value}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              className="text-center w-[260px] flex flex-col items-center justify-center pointer-events-auto"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-amber-500/20 blur-xl rounded-full animate-pulse" />
                <div className="relative p-7 border-2 border-amber-500/40 rounded-full bg-black/80 backdrop-blur-md shadow-[0_0_30px_rgba(123,97,53,0.3)]">
                  <activeGoal.icon className="w-12 h-12 text-amber-400 drop-shadow-[0_0_10px_rgba(251,191,36,0.6)]" />
                </div>
              </div>

              <h3 className="text-xl font-bold text-white uppercase tracking-wider mb-2">{activeGoal.title}</h3>
              <p className="text-amber-200/60 text-xs leading-relaxed mb-6 font-mono px-4">{activeGoal.description}</p>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onGoalSelect(activeGoal.value)}
                className={`px-8 py-2 rounded-full border text-xs font-bold tracking-widest uppercase transition-all duration-300 ${
                  selectedGoal === activeGoal.value
                    ? 'bg-amber-500 text-black border-amber-500 shadow-[0_0_20px_rgba(245,158,11,0.5)]'
                    : 'bg-transparent text-amber-500 border-amber-500/50 hover:border-amber-400'
                }`}
              >
                {selectedGoal === activeGoal.value ? 'SELECTED' : 'INITIATE'}
              </motion.button>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Goal Nodes Ring */}
        <div className="absolute inset-0">
          {goals.map((goal, index) => {
            const pos = getPosition(index);
            const isSelected = selectedGoal === goal.value;
            const isHovered = hoveredGoal === goal.value;
            const isActive = isSelected || isHovered;

            return (
              <motion.button
                key={goal.value}
                className="absolute w-12 h-12 -ml-6 -mt-6 rounded-full flex items-center justify-center z-30 group"
                style={{ left: '50%', top: '50%', x: pos.x, y: pos.y }}
                onMouseEnter={() => setHoveredGoal(goal.value)}
                onMouseLeave={() => setHoveredGoal(null)}
                onClick={() => onGoalSelect(goal.value)}
                whileHover={{ scale: 1.15 }}
              >
                {/* Anti-clockwise Sequential Pulse Background */}
                <motion.div 
                  animate={{ 
                    opacity: [0.1, 0.8, 0.1],
                    scale: [0.9, 1.05, 0.9]
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    delay: (totalGoals - index) * 0.15, // Anti-clockwise timing
                    ease: "easeInOut" 
                  }}
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{ background: 'linear-gradient(106.34deg, #7b6135, #000)' }}
                />

                <div className={`relative w-full h-full rounded-full flex items-center justify-center transition-all duration-300 border ${
                  isSelected ? 'bg-amber-500 border-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.7)]' : 
                  isHovered ? 'bg-amber-900/60 border-amber-400' : 'bg-black/60 border-amber-500/20 group-hover:border-amber-500/50'
                }`}>
                  <goal.icon className={`w-5 h-5 transition-colors ${isSelected ? 'text-black' : 'text-amber-500/80'}`} />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Control Panel (Right Side) */}
      <div className="flex flex-col items-start lg:w-[400px] space-y-8 z-10 text-center lg:text-left pt-8 lg:pt-0">
        <motion.div
           initial={{ opacity: 0, x: 20 }}
           animate={{ opacity: 1, x: 0 }}
           className="space-y-4"
        >
          <div className="inline-block p-1 px-3 bg-amber-500/10 border border-amber-500/20 rounded-md mb-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-amber-500 font-bold font-mono">Pricing Quiz</span>
          </div>
          <h2 className="text-6xl font-black text-white tracking-tight leading-tight">
            Select <span className="text-amber-500">Goal</span>
          </h2>
          <p className="text-gray-400 text-lg font-medium leading-relaxed">
             We will recommend the best video for <span className="text-white">maximum ROI</span>.
          </p>
        </motion.div>

        <motion.button
          whileHover={{ scale: 1.05, x: 10 }}
          whileTap={{ scale: 0.95 }}
          onClick={onNext}
          disabled={!selectedGoal}
          className={`group flex items-center gap-6 px-12 py-5 rounded-2xl border-2 transition-all duration-300 ${
            selectedGoal
              ? 'bg-amber-500 border-amber-400 text-black shadow-[0_15px_50px_-10px_rgba(245,158,11,0.5)]'
              : 'bg-white/5 border-white/10 text-gray-500 cursor-not-allowed opacity-50'
          }`}
        >
          <span className="font-bold uppercase tracking-[0.2em] text-xl">Next</span>
          <ChevronRight className={`w-7 h-7 transition-transform group-hover:translate-x-1 ${selectedGoal ? 'text-black' : 'text-gray-600'}`} />
        </motion.button>

        {/* System Diagnostics Decoration */}
        <div className="w-full pt-10 border-t border-white/10 hidden lg:block">
          <div className="flex items-center gap-8">
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-mono">System Integrity</p>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(i => <div key={i} className="w-4 h-1 bg-amber-500/40 rounded-full" />)}
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-mono">Node Sync</p>
              <p className="text-white font-mono text-xs uppercase">Active Sequence</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default StepOne;